import {
  CAT_PLANS,
  DOG_PLANS,
  OYEN_FAQ,
  PLAN_BILL_CYCLE,
  PLAN_TYPE,
  SHOW_NEW_MSIG_POLICY_WORDING,
} from '@utils/constant';

import Button from '@components/Button';
import Icon from '@components/Icon';
import { priceFormatted } from '@utils/priceBreakdown';
import { Tag } from 'antd';
import { cn } from '@lib/utils';
import { i18n, useTranslation } from 'next-i18next';
import Image from 'next/image';
import React from 'react';
import Tooltip from './ToolTip';
import KotakafulIcon from '@assets/images/kotakaful.svg';
import KotakafulDeduktibel from '@assets/images/kotakaful-deductibiles.svg';
import KotakafulDeduktibelBM from '@assets/images/kotakaful-deductibiles-bm.svg';
import Modal from './Modal';

export type PlanCardProps = {
  id?: number | string;
  plan: string;
  image: any;
  billCycle: PLAN_BILL_CYCLE | undefined;
  yearlyPrice: number;
  monthlyPrice: number;
  medicalCost: number;
  overnightStay: number | null;
  followUpTreatment: number;
  thirdPartyDamages: number;
  cremationCost: number;
  deductiblesAmount?: number;
  freeMicroChip: boolean | null;
  currency?: string;
  loadingPercent?: number | null | undefined;
  upgradeLimit?: boolean;
  disabled?: boolean;
  purchaseType?: PLAN_TYPE;
  onClick?: () => void;
  vetMedicalLimit?: number;
  thirdPartyLimit?: number;
  burialLimit?: number;
  name?: string;
};

const reformatPlanName = (value: string | null) => {
  if (!value) throw new Error('Invalid plan name');

  const planName = value.toLowerCase();

  if (planName.includes('basic')) {
    return 'planBasic';
  }

  if (planName.includes('plus')) {
    return 'planPlus';
  }

  if (planName.includes('champion')) {
    return 'planChampion';
  }

  throw new Error('Invalid plan name passed');
};

function PlanCard({
  plan,
  image,
  billCycle,
  yearlyPrice,
  monthlyPrice,
  medicalCost,
  overnightStay,
  followUpTreatment,
  thirdPartyDamages,
  cremationCost,
  freeMicroChip,
  deductiblesAmount,
  upgradeLimit,
  currency = 'MYR',
  disabled,
  onClick,
  purchaseType,
}: PlanCardProps) {
  const { t } = useTranslation('planCard');
  const { t: tc } = useTranslation('common');

  const originalYearlyPrice = monthlyPrice * 12;
  const planPrice =
    billCycle === PLAN_BILL_CYCLE.YEARLY ? yearlyPrice : monthlyPrice;

  const [opened, setOpened] = React.useState(false);

  const details = (
    description?: string | React.ReactNode | React.ReactNode[],
    value?: string | any
  ) => (
    <div className="plan-card-detail-row">
      <span className="description text-gray-500">{description}</span>
      {value && value === '-' && (
        <span className="text-gray-400">{tc('none')}</span>
      )}
      {value && value !== '-' && typeof value === 'string' && (
        <span className="value">{value}</span>
      )}
      {value && value !== '-' && typeof value === 'boolean' && (
        <Icon name="tick" color="green" size={1.3} />
      )}
    </div>
  );

  const renderSavingPercentage = (currentPlan: string) => {
    let saving;

    switch (currentPlan) {
      case CAT_PLANS.CAT_BASIC:
        saving = '28';
        break;
      case DOG_PLANS.DOG_PLUS:
        saving = '22';
        break;
      case DOG_PLANS.DOG_CHAMPION:
        saving = '20';
        break;
      default:
        saving = '23';
    }

    return (
      <div className="flex justify-center">
        <h6 className="plan-card-header-save">
          {t('save')}
          {saving}
          %!
        </h6>
      </div>
    );
  };

  const renderPricing = (
    billCycleSelected: string | undefined,
    currentPlan: string
  ) => {
    switch (billCycleSelected) {
      case PLAN_BILL_CYCLE.MONTHLY:
        return (
          <div className="">
            <p className="plan-card-header-price">
              <span className="plan-card-header-amount">
                {`${priceFormatted(monthlyPrice, currency)}`}
                <p className="inline-block text-xs font-normal">
                  /{t('month')}
                </p>
              </span>
            </p>
          </div>
        );
      default:
        return purchaseType === PLAN_TYPE.RENEWAL ? (
          <div className="">
            <div className="plan-card-header-price">
              <span className="plan-card-header-amount">
                {priceFormatted(yearlyPrice, currency)}
                <p className="inline-block text-xs font-normal">/{t('year')}</p>
              </span>
            </div>
          </div>
        ) : (
          <div className="">
            <p className="plan-card-header-price">
              <span className="plan-card-header-original-amount">
                {priceFormatted(originalYearlyPrice, currency)}
              </span>
              <span className="plan-card-header-amount">
                {priceFormatted(yearlyPrice, currency)}
              </span>
              <p className="inline-block text-xs font-normal">/{t('year')}</p>
            </p>
            {renderSavingPercentage(currentPlan)}
          </div>
        );
    }
  };

  const detailUpgrade = (description?: string) => (
    <div className="bg-secondary-100 rounded-xl p-2 my-1 relative">
      <div className="flex justify-between">
        <span className="description flex items-end text-xs">
          {description}
          <Tooltip
            text={t('choose_upgrade_limit')}
            buttonText={t('learn_more')}
            url={OYEN_FAQ.CLAIM_LIMIT_UP_DOWNGRADE}
          >
            {t('what_this')}
          </Tooltip>
        </span>
      </div>
      <div className="text-xs font-light mt-2">{t('illnesses_diagnose')}</div>
    </div>
  );

  const detailDeductible = (description?: string, value?: string | any) => (
    <div className="plan-card-detail-row relative">
      <span className="description flex items-center">
        {description}
        <Tooltip
          text={t('fixed_amount_reimbursed_claim')}
          buttonText={t('learn_more')}
          url={OYEN_FAQ.DEDUCTIBLE}
        >
          {t('what_this')}
        </Tooltip>
      </span>
      <span className="value">{value}</span>
    </div>
  );

  // if takaful is in the url
  const isTakaful = plan.includes('Takaful');

  return (
    <article
      className={cn(
        'max-w-[90vw] w-[98%] md:min-w-[30%] plan-card rounded-xl relative mx-auto flex flex-col ',
        purchaseType === PLAN_TYPE.RENEWAL && 'plan-card-plan-plus',
        disabled ? 'plan-card-selected' : 'plan-card-unselected'
      )}
    >
      {purchaseType === PLAN_TYPE.RENEWAL ? (
        <>
          <div className="flex align-center w-full">
            <div className="w-[56px] h-full m-auto">
              <Image src={image} layout="responsive" alt="renewal" />
            </div>
            <header className="w-7/12 plan-card-header flex-1 text-left ml-4 gap-0">
              <h4 className="plan-card-header-name">{plan}</h4>
              {renderPricing(billCycle, plan)}
            </header>
          </div>
        </>
      ) : (
        <>
          <div
            className={cn(
              'flex px-4 py-2 rounded-t-xl border-b border-b-gray-300',
              disabled ? 'bg-secondary-50' : 'bg-primary-25'
            )}
            onClick={onClick}
            onKeyUp={onClick}
            role="button"
            tabIndex={0}
          >
            <div className="w-12 h-12">
              <Image
                className="border border-gray-300 w-full h-full bg-cover"
                src={image}
                layout="responsive"
                alt="plan image"
              />
            </div>
            <div className="pl-2">
              <h2 className="text-sm font-medium text-primary">
                {isTakaful
                  ? t(reformatPlanName(plan), {
                      petType: 'Kucing',
                    }) + ' Takaful'
                  : t(reformatPlanName(plan), {
                      petType: plan.includes('Cat') ? tc('cat') : tc('dog'),
                    })}
              </h2>
              <h2 className="font-medium text-xl">
                {`RM${planPrice}`}
                <span className="font-normal text-sm">
                  /
                  {billCycle === PLAN_BILL_CYCLE.YEARLY
                    ? t('year')
                    : t('month')}
                </span>
              </h2>
              {billCycle === PLAN_BILL_CYCLE.YEARLY && freeMicroChip && (
                <Tag color="#26AAFA" className="font-medium">
                  {t('free_micro_chip')}
                </Tag>
              )}
            </div>

            {disabled && (
              <div className="absolute right-3 top-2">
                <Icon
                  name="tick"
                  color="secondary"
                  size={1.5}
                  className="float-right"
                />
              </div>
            )}
          </div>
        </>
      )}
      <section
        className="plan-card-detail"
        onClick={onClick}
        onKeyUp={onClick}
        role="button"
        tabIndex={0}
      >
        {details(
          [
            <div key="medicalcost1">{t('vet_medical_costs')}</div>,
            <div key="medicalcost2">
              {t('vet_medical_costs_reimbursement')}
            </div>,
          ],
          priceFormatted(medicalCost, currency)
        )}

        {upgradeLimit && detailUpgrade(t('upgrade_limit'))}
        {details(
          t('post_surgical_fees'),
          t('days', { value: followUpTreatment })
        )}
        {!isTakaful &&
          SHOW_NEW_MSIG_POLICY_WORDING &&
          details(
            t('non_surgical_follow_up_treatment'),
            t('days', { value: 30 })
          )}
        {details(t('surgical_fees'), true)}
        {details(t('consultation_fees'), true)}
        {details(t('diagnosis_blood_test'), true)}
        {details(t('x-ray_ultrasound'), true)}
        {details(t('prescribed_medication'), true)}

        {details(
          t('clinic_overnight_stays'),
          overnightStay ? overnightStay + ' ' + t('nights') : t('no_cap')
        )}
        {details(
          t('3rd_party_damages'),
          thirdPartyDamages ? priceFormatted(thirdPartyDamages, currency) : '-'
        )}
        {details(
          t('burial_cremation_costs'),
          cremationCost ? priceFormatted(cremationCost, currency) : '-'
        )}
        {(plan === DOG_PLANS.DOG_BASIC ||
          plan === DOG_PLANS.DOG_PLUS ||
          plan === DOG_PLANS.DOG_CHAMPION) &&
          details(
            t('free_micro_chip'),
            freeMicroChip === false || billCycle === PLAN_BILL_CYCLE.MONTHLY
              ? '-'
              : freeMicroChip
          )}
        {!!deductiblesAmount &&
          deductiblesAmount > 0 &&
          detailDeductible(
            t('deductible'),
            priceFormatted(deductiblesAmount / 100, currency)
          )}

        {isTakaful && (
          <>
            <div className="bg-primary-50 m-4 p-2 flex flex-row gap-2 rounded">
              <div className="flex flex-col justify-start align-top">
                <Image
                  src={KotakafulIcon}
                  width={50}
                  height={50}
                  alt="kotakaful icon"
                />
              </div>
              <div>
                <p className="font-normal">{t('youll_pay_10_cotakaful')}</p>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpened(true);
                  }}
                >
                  <a className="text-secondary hover:underline">
                    {t('see_more_cotakaful')}
                  </a>
                </span>
              </div>
            </div>

            <Modal
              onClose={() => setOpened(false)}
              opened={opened}
              title={t('cotakaful_popup_title')}
            >
              <div className="p-4 grid place-items-center">
                {i18n?.resolvedLanguage === 'eni' ? (
                  <Image
                    src={KotakafulDeduktibel}
                    width={300}
                    height={225}
                    alt="kotakaful deduktibel"
                  />
                ) : (
                  <Image
                    src={KotakafulDeduktibelBM}
                    width={300}
                    height={225}
                    alt="kotakaful deduktibel bm"
                  />
                )}

                <p className="font-normal mt-5">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: t('cotakaful_description'),
                    }}
                  />
                </p>

                <Button
                  className="w-full mt-5"
                  onClick={() => setOpened(false)}
                >
                  {t('understood')}
                </Button>
              </div>
            </Modal>
          </>
        )}
      </section>

      <footer className="py-6 px-4 static bottom-0 left-0 right-0 mt-auto">
        <Button
          className="w-full relative mt-5 flex-grow justify-center"
          filled
          disabled={disabled}
          onClick={onClick}
        >
          {disabled ? (
            <div className="absolute left-4">
              <Icon name="check" />
            </div>
          ) : (
            ''
          )}
          {disabled ? t('plan_selected') : t('choose_this')}
        </Button>
      </footer>
    </article>
  );
}

export default PlanCard;
